(() => {
  const CACHE_PREFIX = 'precioux-pincode-v1:';
  const CACHE_TTL = 24 * 60 * 60 * 1000;
  const LEAFLET_VERSION = '1.9.4';
  let leafletPromise;

  const text = (value) => String(value || '').trim();
  const escapeHtml = (value) => String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  function fetchJson(url, timeout = 10000) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout);
    return fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .finally(() => window.clearTimeout(timer));
  }

  function haversine(lat1, lon1, lat2, lon2) {
    const radians = (degrees) => degrees * Math.PI / 180;
    const earthRadiusKm = 6371;
    const deltaLat = radians(lat2 - lat1);
    const deltaLon = radians(lon2 - lon1);
    const a = Math.sin(deltaLat / 2) ** 2
      + Math.cos(radians(lat1)) * Math.cos(radians(lat2)) * Math.sin(deltaLon / 2) ** 2;
    return 2 * earthRadiusKm * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function formatDuration(seconds) {
    const minutes = Math.max(1, Math.round(seconds / 60));
    if (minutes < 60) return `Approx. ${minutes} min drive`;
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    return `Approx. ${hours} hr${hours === 1 ? '' : 's'}${remainder ? ` ${remainder} min` : ''} drive`;
  }

  function getCache(pin) {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_PREFIX + pin));
      if (cached && Date.now() - cached.savedAt < CACHE_TTL) return cached.value;
      sessionStorage.removeItem(CACHE_PREFIX + pin);
    } catch (_) {
      return null;
    }
    return null;
  }

  function setCache(pin, value) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + pin, JSON.stringify({ savedAt: Date.now(), value }));
    } catch (_) {
      // Storage can be unavailable in private browsing; the checker still works.
    }
  }

  async function lookupPostalArea(pin) {
    const payload = await fetchJson(`https://api.postalpincode.in/pincode/${encodeURIComponent(pin)}`);
    const response = Array.isArray(payload) ? payload[0] : null;
    if (!response || response.Status !== 'Success' || !response.PostOffice?.length) {
      const error = new Error('PIN_NOT_FOUND');
      error.code = 'PIN_NOT_FOUND';
      throw error;
    }
    const office = response.PostOffice.find((item) => item.DeliveryStatus === 'Delivery') || response.PostOffice[0];
    return {
      pin,
      area: text(office.Name),
      city: text(office.Block) || text(office.Division) || text(office.District),
      district: text(office.District),
      state: text(office.State),
    };
  }

  async function geocodePostalArea(postal) {
    const params = new URLSearchParams({
      format: 'jsonv2',
      country: 'India',
      postalcode: postal.pin,
      limit: '1',
      addressdetails: '1',
    });
    let results = await fetchJson(`https://nominatim.openstreetmap.org/search?${params}`);
    if (!Array.isArray(results) || !results.length) {
      const fallback = new URLSearchParams({
        format: 'jsonv2',
        q: [postal.area, postal.district, postal.state, postal.pin, 'India'].filter(Boolean).join(', '),
        limit: '1',
        addressdetails: '1',
      });
      results = await fetchJson(`https://nominatim.openstreetmap.org/search?${fallback}`);
    }
    if (!Array.isArray(results) || !results.length) throw new Error('GEOCODE_FAILED');
    const latitude = Number(results[0].lat);
    const longitude = Number(results[0].lon);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) throw new Error('GEOCODE_FAILED');
    return { latitude, longitude, address: results[0].address || {} };
  }

  function postalFromGeocode(pin, coordinates) {
    const address = coordinates.address || {};
    const area = text(address.city) || text(address.town) || text(address.village)
      || text(address.suburb) || text(address.county) || text(address.state_district);
    return {
      pin,
      area,
      city: text(address.city) || text(address.town) || text(address.county) || area,
      district: text(address.state_district) || text(address.county) || area,
      state: text(address.state),
    };
  }

  async function getRoute(customer, store) {
    const coordinates = `${customer.longitude},${customer.latitude};${store.longitude},${store.latitude}`;
    const url = `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full&geometries=geojson&steps=false`;
    const payload = await fetchJson(url, 12000);
    const route = payload?.routes?.[0];
    if (!route || !Number.isFinite(route.distance)) throw new Error('ROUTE_FAILED');
    return {
      distanceKm: route.distance / 1000,
      durationSeconds: route.duration,
      geometry: route.geometry,
      approximate: false,
    };
  }

  function loadLeaflet() {
    if (window.L) return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;
    leafletPromise = new Promise((resolve, reject) => {
      let cssReady = false;
      let jsReady = false;
      const checkReady = () => {
        if (cssReady && jsReady && window.L) resolve(window.L);
      };

      if (!document.querySelector('link[data-precioux-leaflet]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
        link.crossOrigin = '';
        link.dataset.preciouxLeaflet = '';
        link.onload = () => { cssReady = true; checkReady(); };
        link.onerror = () => { cssReady = true; checkReady(); };
        document.head.appendChild(link);
      } else {
        cssReady = true;
      }

      const script = document.createElement('script');
      script.src = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;
      script.crossOrigin = '';
      script.onload = () => { jsReady = true; checkReady(); };
      script.onerror = () => reject(new Error('MAP_LOAD_FAILED'));
      document.head.appendChild(script);
    });
    return leafletPromise;
  }

  class PincodeChecker {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-pincode-form]');
      this.input = root.querySelector('[data-pincode-input]');
      this.submit = root.querySelector('[data-pincode-submit]');
      this.buttonLabel = root.querySelector('[data-button-label]');
      this.message = root.querySelector('[data-pincode-message]');
      this.result = root.querySelector('[data-pincode-result]');
      this.mapElement = root.querySelector('[data-pincode-map]');
      this.defaultButtonText = this.buttonLabel.textContent;
      this.inFlight = false;
      this.map = null;
      this.layerGroup = null;
      this.config = this.readConfig();
      this.form.addEventListener('submit', (event) => this.submitForm(event));
      this.input.addEventListener('input', () => {
        this.clearMessage();
      });
    }

    readConfig() {
      const data = this.root.dataset;
      const getVal = (...keys) => {
        for (const k of keys) {
          if (data[k] !== undefined && data[k] !== null && data[k] !== '') return data[k];
        }
        return '';
      };
      const number = (value) => {
        const n = Number.parseFloat(value);
        return Number.isFinite(n) ? n : 0;
      };
      return {
        store: {
          name: data.storeName || 'Precioux Jewellery',
          address: data.storeAddress || '',
          pin: data.storePincode || '',
          latitude: number(data.storeLatitude),
          longitude: number(data.storeLongitude),
        },
        mapEnabled: data.mapEnabled === 'true',
        travelTimeEnabled: data.travelTimeEnabled === 'true',
        zones: [
          {
            max: number(getVal('zone1Max', 'zone-1Max', 'zone-1-max')),
            label: getVal('zone1Label', 'zone-1Label', 'zone-1-label') || 'Same-day delivery available',
          },
          {
            max: number(getVal('zone2Max', 'zone-2Max', 'zone-2-max')),
            label: getVal('zone2Label', 'zone-2Label', 'zone-2-label') || 'Local delivery available',
          },
          {
            max: number(getVal('zone3Max', 'zone-3Max', 'zone-3-max')),
            label: getVal('zone3Label', 'zone-3Label', 'zone-3-label') || 'Standard delivery available',
          },
          {
            max: Infinity,
            label: getVal('zone4Label', 'zone-4Label', 'zone-4-label') || 'Courier delivery available',
          },
        ].sort((a, b) => a.max - b.max),
      };
    }

    validate(pin) {
      if (!pin) return 'Please enter your PIN code.';
      if (!/^\d+$/.test(pin)) return 'Only numeric PIN codes are accepted.';
      if (!/^[1-9]\d{5}$/.test(pin)) return 'Enter a valid 6-digit PIN code.';
      return '';
    }

    clearMessage() {
      this.message.textContent = '';
      this.input.removeAttribute('aria-invalid');
    }

    showError(message) {
      this.message.textContent = message;
      this.input.setAttribute('aria-invalid', 'true');
      this.result.hidden = true;
    }

    setLoading(loading) {
      this.inFlight = loading;
      this.root.classList.toggle('is-loading', loading);
      this.submit.disabled = loading;
      this.input.readOnly = loading;
      this.buttonLabel.textContent = loading ? 'Checking location…' : this.defaultButtonText;
    }

    deliveryLabel(distanceKm, pin) {
      const zone = this.config.zones.find((item) => distanceKm <= item.max) || this.config.zones[this.config.zones.length - 1];
      const label = (zone && zone.label) ? zone.label : 'Standard delivery available';
      return `Delivery available to ${pin} — ${label}`;
    }

    async resolve(pin) {
      const cached = getCache(pin);
      if (cached) return cached;
      const [postalResult, coordinatesResult] = await Promise.allSettled([
        lookupPostalArea(pin),
        geocodePostalArea({ pin }),
      ]);
      let postal = postalResult.status === 'fulfilled' ? postalResult.value : null;
      let coordinates = coordinatesResult.status === 'fulfilled' ? coordinatesResult.value : null;
      if (!coordinates && postal) {
        try {
          coordinates = await geocodePostalArea(postal);
        } catch (_) {
          // Handled below after both coordinate lookup strategies have failed.
        }
      }
      if (!coordinates) {
        if (postalResult.reason?.code === 'PIN_NOT_FOUND') {
          const notFound = new Error('PIN_NOT_FOUND');
          notFound.code = 'PIN_NOT_FOUND';
          throw notFound;
        }
        throw new Error('GEOCODE_FAILED');
      }
      if (!postal) postal = postalFromGeocode(pin, coordinates);
      let route;
      try {
        route = await getRoute(coordinates, this.config.store);
      } catch (_) {
        route = {
          distanceKm: haversine(
            coordinates.latitude,
            coordinates.longitude,
            this.config.store.latitude,
            this.config.store.longitude
          ),
          durationSeconds: null,
          geometry: null,
          approximate: true,
        };
      }
      const resolved = { postal, coordinates, route };
      setCache(pin, resolved);
      return resolved;
    }

    render(data) {
      const { postal, coordinates, route } = data;
      const distance = Math.max(0, route.distanceKm);
      this.root.querySelector('[data-result-place]').textContent = [postal.area, postal.state].filter(Boolean).join(', ');
      this.root.querySelector('[data-result-pincode]').textContent = postal.pin;
      this.root.querySelector('[data-result-area]').textContent = postal.area || postal.city || '—';
      this.root.querySelector('[data-result-district]').textContent = postal.district || '—';
      this.root.querySelector('[data-result-state]').textContent = postal.state || '—';
      this.root.querySelector('[data-result-distance]').textContent = distance < 10 ? distance.toFixed(1) : Math.round(distance).toLocaleString('en-IN');
      this.root.querySelector('[data-distance-label]').textContent = route.approximate ? 'Approximate distance from our store' : 'Driving distance from our store';
      const duration = this.root.querySelector('[data-result-duration]');
      duration.hidden = !this.config.travelTimeEnabled || !route.durationSeconds;
      duration.textContent = route.durationSeconds ? formatDuration(route.durationSeconds) : '';
      this.root.querySelector('[data-result-delivery]').textContent = this.deliveryLabel(distance, postal.pin);
      this.root.querySelector('[data-approx-note]').hidden = !route.approximate;
      const directions = new URL('https://www.google.com/maps/dir/');
      directions.searchParams.set('api', '1');
      directions.searchParams.set('origin', `${coordinates.latitude},${coordinates.longitude}`);
      directions.searchParams.set('destination', `${this.config.store.latitude},${this.config.store.longitude}`);
      directions.searchParams.set('travelmode', 'driving');
      this.root.querySelector('[data-directions-link]').href = directions.toString();
      this.result.hidden = false;
      this.result.focus({ preventScroll: true });
      if (this.config.mapEnabled) this.renderMap(data);
    }

    async renderMap(data) {
      try {
        const L = await loadLeaflet();
        this.mapElement.hidden = false;
        this.root.querySelector('[data-map-note]').hidden = false;

        // Ensure container is rendered in layout
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

        if (!this.map) {
          this.map = L.map(this.mapElement, { scrollWheelZoom: false });
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          }).addTo(this.map);
          this.layerGroup = L.layerGroup().addTo(this.map);
        }
        this.layerGroup.clearLayers();
        const customer = [data.coordinates.latitude, data.coordinates.longitude];
        const store = [this.config.store.latitude, this.config.store.longitude];
        const customerMarker = L.circleMarker(customer, { radius: 8, color: '#3b2118', fillColor: '#faf7f0', fillOpacity: 1, weight: 3 })
          .bindPopup(`<strong>Your PIN Code Area</strong><br>${escapeHtml(data.postal.area)}, ${escapeHtml(data.postal.state)}`);
        const storeMarker = L.circleMarker(store, { radius: 9, color: '#3b2118', fillColor: '#c9a54b', fillOpacity: 1, weight: 3 })
          .bindPopup(`<strong>${escapeHtml(this.config.store.name)}</strong><br>${escapeHtml(this.config.store.address)}`);
        this.layerGroup.addLayer(customerMarker).addLayer(storeMarker);
        if (data.route.geometry) {
          this.layerGroup.addLayer(L.geoJSON(data.route.geometry, { style: { color: '#9c702f', weight: 4, opacity: .8 } }));
        } else {
          this.layerGroup.addLayer(L.polyline([customer, store], { color: '#9c702f', weight: 3, opacity: .65, dashArray: '7 8' }));
        }
        this.map.invalidateSize();
        this.map.fitBounds(L.latLngBounds([customer, store]), { padding: [34, 34], maxZoom: 13 });
        window.setTimeout(() => {
          if (this.map) {
            this.map.invalidateSize();
            this.map.fitBounds(L.latLngBounds([customer, store]), { padding: [34, 34], maxZoom: 13 });
          }
        }, 200);
      } catch (_) {
        this.mapElement.hidden = true;
        this.root.querySelector('[data-map-note]').hidden = true;
      }
    }

    async submitForm(event) {
      event.preventDefault();
      if (this.inFlight) return;
      const pin = this.input.value.trim();
      const validationMessage = this.validate(pin);
      if (validationMessage) {
        this.showError(validationMessage);
        return;
      }
      const store = this.config.store;
      if (![store.latitude, store.longitude].every(Number.isFinite)) {
        this.showError('Store location is not configured. Please contact us for assistance.');
        return;
      }
      this.clearMessage();
      this.setLoading(true);
      try {
        this.render(await this.resolve(pin));
      } catch (error) {
        if (error?.code === 'PIN_NOT_FOUND') {
          this.showError("We couldn't find this PIN code. Please check it and try again.");
        } else if (error?.name === 'AbortError' || !navigator.onLine) {
          this.showError('The location service took too long to respond. Check your connection and try again.');
        } else {
          this.showError("We're unable to check this location right now. Please try again.");
        }
      } finally {
        this.setLoading(false);
      }
    }
  }

  function initialise(scope = document) {
    scope.querySelectorAll('[data-pincode-checker]').forEach((root) => {
      if (root.dataset.initialised === 'true') return;
      root.dataset.initialised = 'true';
      new PincodeChecker(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
  else initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
